import { useMemo } from "react";
import useSWR from "swr";

import { usePaginationControls } from "@ds-cps/ui";

import {
  OrganizationUserDetails,
  OrganizationUserDetailsResponse
} from "@organization/models/OrganizationUserDetails";

import { associateUserEndpoints } from "@api/Endpoints";
import { fetcher } from "@utils/swr-utils";

import { CreateParamsForGetAdmins } from "./CreateParamsForGetAdmins";

export function useGetOrganizationAdmins(organizationId: string) {
  const { pageIndex, pageSize } = usePaginationControls();

  const params = new CreateParamsForGetAdmins();
  params.withPagination(pageSize, pageIndex);
  // Tech Note. In main component, sorting of table entries done via JavaScript aka Frontend Sorting

  const url =
    associateUserEndpoints.get(organizationId) + `&${params.toString()}`;

  const { data, error, isLoading, mutate } =
    useSWR<OrganizationUserDetailsResponse>(url, fetcher);

  return useMemo(
    () => ({ admins: data?.users, isLoading, error, mutate }),
    [data, isLoading, error, mutate]
  );
}

export function filter(
  admins: OrganizationUserDetails[] | undefined,
  searchValue: string | undefined
): OrganizationUserDetails[] {
  if (!admins) return [];

  if (!searchValue) return admins;

  const value = searchValue.toLocaleLowerCase();

  return admins.filter(
    item =>
      item.displayName.toLocaleLowerCase().includes(value) ||
      item.emailAddress.toLocaleLowerCase().includes(value) ||
      item.state.toLocaleLowerCase().includes(value)
  );
}

export function sort(
  admins: OrganizationUserDetails[] | null,
  activeSort: string | undefined,
  sortDirection: "ascending" | "descending" | undefined
): OrganizationUserDetails[] {
  if (!admins) return [];

  if (!activeSort) return admins;

  const result = [...admins].sort(
    (a: OrganizationUserDetails, b: OrganizationUserDetails): number => {
      let nameA: string | number | undefined;
      let nameB: string | number | undefined;
      switch (activeSort) {
        case "name":
          nameA = a.displayName;
          nameB = b.displayName;
          break;
        case "email":
          nameA = a.emailAddress;
          nameB = b.emailAddress;
          break;
        case "invited-on":
          nameA = getTime(new Date(a.createdOn));
          nameB = getTime(new Date(b.createdOn));
          break;
      }

      if (typeof nameA === "string" && typeof nameB === "string") {
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      }
      return 0;
    }
  );

  if (sortDirection === "descending") return result.reverse();

  return result;
}

function getTime(date?: Date) {
  return date?.getTime() || 0;
}
